import type { ArticleFeature } from '~kui/components/KUI/Unified/Content/types';

import { formatCalendarDay } from '~kui/utils/localeFormat';

import type { ArticleCard } from '~/composables/unified/content/articleCards';

import { useClusterCountryConfig } from '~/composables/core';
import { toArticleCard } from '~/composables/unified/content/articleCards';
import { useLoggedFailure } from '~/composables/unified/useLoggedFailure';
import { fetchFeaturedMedia } from '~/graphql/documents/learn/articles';
import { fromCampaignDateTime } from '~/utils/campaign';

/**
 * The articles admins have placed in Featured media, in their order, for the Media centre: the
 * first leads the page with its standfirst and byline, the rest follow as cards.
 */
export const useFeaturedMedia = () => {
  const { t, locale } = useI18n();
  const { $urqlClient } = useNuxtApp();
  const { clusterTimezone } = useClusterCountryConfig();

  const { data, pending, error } = useAsyncData(
    'media-centre-featured-media',
    async () => {
      const result = await $urqlClient.query(fetchFeaturedMedia, {}).toPromise();
      if (result.error) {
        throw result.error;
      }
      return result.data?.learningAssetsInCategory ?? [];
    },
    { server: true },
  );

  useLoggedFailure({ error, message: 'Failed to load featured media' });

  const lead = computed<ArticleFeature | null>(() => {
    const asset = data.value?.[0];
    if (!asset) {
      return null;
    }
    const publishedDay = fromCampaignDateTime(asset.publishedAt, clusterTimezone.value);
    return {
      ...toArticleCard(asset, { t }),
      standfirst: asset.publishedContent?.articleData?.standfirst ?? null,
      authorName: asset.publishedContent?.articleData?.authorName ?? null,
      publishedOn: publishedDay ? formatCalendarDay({ date: publishedDay, locale: locale.value, withYear: true }) : null,
    };
  });

  const articles = computed<ArticleCard[]>(() => (data.value ?? []).slice(1).map(asset => toArticleCard(asset, { t })));

  return { lead, articles, pending, error };
};
